import { TCanvas } from './Canvas'
import { patternInitialStateLogo } from './initialAssets/onlineEditorInitialStateLogo'
import { ILogoWidget } from './Widgets/LogoWidget'
import { IPatternWidget, TPatternWidgetVariant } from './Widgets/PatternWidget'

const ARTBOARD_OFFSET_LG = 50
const ARTBOARD_OFFSET_MD = 30
const ARTBOARD_SIZE = {
  width: 510,
  height: 460,
}
export const initialMultiColorAssetUrl =
  'https://images.ctfassets.net/5gvckmvm9289/67q6NX9pNi5CTYv6AslqYx/0ba6cf746e2a7de215b5a24d7f9085ea/svg.svg'

export const PATTERN_VARIANTS: Record<string, TPatternWidgetVariant> = {
  LINE: 'one line pattern',
  TILE: 'tile pattern',
}

export const EDITOR_CONFIG_FACTOR = {
  ASSET_SIZE_RATIO: 0.073,
  TRANSITION_OFFSET_RATIO: 0.02,
  SCALE_UP: 1.05,
  SCALE_DOWN: 0.95,
}
const EMPTY_COLOR_STATE = {
  value: '#FFFFFF',
  label: 'White',
}
const INITIAL_ACCENT_COLOR = {
  value: '#000000',
  label: 'Black',
}
const INITIAL_BACKGROUND_COLOR = {
  value: '#FFFFFF',
  label: 'White',
}
// This color used only for demo purpose and won't get into print.
// That's why it's not part of the pantone C book and doesn't have label.
const INITIAL_KRAFT_COLOR = {
  value: '#D0BC9B',
  label: '',
}

const initialCanvasConfig: TCanvas = {
  shape: 'rectangle',
  width: 400,
  height: 300,
  bleedHeight: 10,
  bleedWidth: 10,
  backgroundColor: EMPTY_COLOR_STATE,
  logoColor: INITIAL_ACCENT_COLOR,
  canvasSvgData: '',
  activeWidgetId: null,
  activeWidgetType: 'pattern',
}

const initialLogoWidgetConfig: ILogoWidget = {
  position: {
    x: undefined,
    y: undefined,
  },
  positionKey: 'center',
  size: {
    w: 0,
    h: 0,
  },
  rotation: 0,
  assetSvgData: patternInitialStateLogo,
  assetUrl: initialMultiColorAssetUrl,
  color: 'purple',
  scaleStepCount: 1,
}

export function getMargins({
  assetWidth,
  horizontalMarginStepCount,
  assetHeight,
  verticalMarginStepCount,
  patternVariant,
  canvasHeight,
}: {
  assetWidth: number
  horizontalMarginStepCount: number
  assetHeight: number
  verticalMarginStepCount: number
  patternVariant: TPatternWidgetVariant
  canvasHeight: number
}): {
  horizontalMargin: number
  verticalMargin: number
} {
  const horizontalMargin = 0.1 * assetWidth * horizontalMarginStepCount
  let verticalMargin = 0

  if (patternVariant === PATTERN_VARIANTS.LINE) {
    // Setting large value [canvasHeight] to make sure that we have only one line of logos/pattern pieces in a view
    verticalMargin = canvasHeight
  } else {
    verticalMargin = 0.1 * assetHeight * verticalMarginStepCount
  }

  return { horizontalMargin, verticalMargin }
}

export function calculateAssetSize(
  canvasConfig: Pick<TCanvas, 'width' | 'height'> = { width: 510, height: 70 },
  scaleStepCount = 0,
  initialSize: {
    w: number
    h: number
  }
): { assetWidth: number; assetHeight: number } {
  if (
    !canvasConfig ||
    canvasConfig.width === undefined ||
    scaleStepCount === undefined
  ) {
    return {
      assetWidth: undefined,
      assetHeight: undefined,
    }
  }

  const scaleFactor =
    scaleStepCount > 0
      ? Math.pow(EDITOR_CONFIG_FACTOR.SCALE_UP, scaleStepCount)
      : Math.pow(EDITOR_CONFIG_FACTOR.SCALE_DOWN, -scaleStepCount)

  return {
    assetWidth: initialSize?.w * scaleFactor,
    assetHeight: initialSize?.h * scaleFactor,
  }
}

export function calculatePatternWidgetStartPosition({
  patternWidgetConfig,
  canvasConfig,
  assetSize,
  margins,
}: {
  patternWidgetConfig: Pick<
    IPatternWidget,
    'verticalPositionStepCount' | 'horizontalPositionStepCount' | 'variant'
  >
  canvasConfig: TCanvas
  assetSize: {
    w: number
    h: number
  }
  margins: {
    horizontal: number
    vertical: number
  }
}) {
  const positionOffset =
    (canvasConfig?.width || 0) * EDITOR_CONFIG_FACTOR.TRANSITION_OFFSET_RATIO

  const { horizontalPositionStepCount, verticalPositionStepCount, variant } =
    patternWidgetConfig

  // We move the pattern start position 1 logo to the left and up. This is so users can move the pattern in any direction and the
  // pattern will not
  const xPositionSubtractOnePattern = margins?.horizontal + assetSize?.w
  const yPositionSubtractOnePattern = margins?.vertical + assetSize?.h
  let xStartPosition = 0
  let yStartPosition = 0

  if (variant === PATTERN_VARIANTS.LINE) {
    xStartPosition = margins?.horizontal / 2
    yStartPosition = (canvasConfig?.height - assetSize?.h) / 2
  } else {
    xStartPosition =
      margins?.horizontal / 2 +
      canvasConfig.bleedWidth -
      xPositionSubtractOnePattern +
      positionOffset * horizontalPositionStepCount

    yStartPosition =
      margins?.vertical / 2 +
      canvasConfig.bleedHeight -
      yPositionSubtractOnePattern +
      positionOffset * verticalPositionStepCount
  }

  return { xStartPosition, yStartPosition }
}

export function initialPatternWidgetWithoutSvgConfig(): Omit<
  IPatternWidget,
  'assetSvgData'
> {
  return {
    offset: false,
    color: '#282828',
    rotationAngle: 0,
    horizontalPositionStepCount: 0,
    verticalPositionStepCount: 0,
    scaleStepCount: 0,
    horizontalMarginStepCount: 12,
    verticalMarginStepCount: 14,
  }
}

export function initialPatternWidgetConfig(): IPatternWidget {
  return {
    ...initialPatternWidgetWithoutSvgConfig(),
    assetSvgData: patternInitialStateLogo,
  }
}

export function calculatePositionOffset(canvasWidth): number {
  return canvasWidth * EDITOR_CONFIG_FACTOR.TRANSITION_OFFSET_RATIO
}

export {
  initialCanvasConfig,
  initialLogoWidgetConfig,
  ARTBOARD_SIZE,
  ARTBOARD_OFFSET_MD,
  ARTBOARD_OFFSET_LG,
  EMPTY_COLOR_STATE,
  INITIAL_BACKGROUND_COLOR,
  INITIAL_ACCENT_COLOR,
  INITIAL_KRAFT_COLOR,
}
